import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  adminPreparedOrderKeys,
  invalidateFulfillmentQuotes,
  createAdminPreparedOrder,
  mapAdminPreparedOrderError,
  useTranslation,
} from '@dukanti/shared';
import type {
  CreateAdminPreparedOrderInput,
  CreateAdminPreparedOrderResult,
  TranslationKey,
} from '@dukanti/shared';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

export function useCreateAdminPreparedOrder(
  storeId: string | null,
): UseMutationResult<CreateAdminPreparedOrderResult, unknown, CreateAdminPreparedOrderInput> {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<CreateAdminPreparedOrderResult, unknown, CreateAdminPreparedOrderInput>({
    retry: false,
    mutationKey: ['create-admin-prepared-order'] as const,
    mutationFn: (input) => createAdminPreparedOrder(supabase, input),
    onSuccess: (result) => {
      if (storeId) void invalidateFulfillmentQuotes(queryClient, storeId);
      if (!result.success) {
        toast.error(t(mapAdminPreparedOrderError(undefined, result) as TranslationKey));
        return;
      }
      if (storeId) {
        void queryClient.invalidateQueries({ queryKey: ['store-orders', storeId] });
        void queryClient.invalidateQueries({ queryKey: ['stock-summary', storeId] });
      }
      void queryClient.invalidateQueries({ queryKey: ['incoming-orders-count-user'] });
      void queryClient.invalidateQueries({ queryKey: adminPreparedOrderKeys.all });
    },
    onError: (error) => {
      toast.error(t(mapAdminPreparedOrderError(error) as TranslationKey));
    },
  });
}
